/**
 * RTWO Core Engine — Architecture Reference Store
 * Version: 1.0 Enterprise Edition
 *
 * RTWO TRUTHFUL DATA POLICY NOTICE:
 * This file contains structural/architectural reference data only.
 * It defines service topology, layer configuration, and system architecture.
 * It does NOT contain real blockchain data (blocks, transactions, validators,
 * balances, TPS, peer counts, governance votes, or staking rewards).
 *
 * All live blockchain metrics must come from real infrastructure:
 *   - Real RPC endpoint
 *   - Real Database
 *   - Real Validator Node
 *   - Real Indexer
 *   - Real Wallet Service
 *
 * RTWO_ALLOW_DEMO_DATA=false
 * RTWO_REQUIRE_REAL_DATA=true
 */

import type {
  Block,
  Transaction,
  Validator,
  NetworkNode,
  GovernanceProposal,
  Alert,
  SecurityEvent,
  AuditEntry,
  SystemMetrics,
  RpcEndpoint,
  ApiKey,
  AutomationRule,
  SlashEvent,
  EpochInfo,
  MempoolState,
  NetworkTopology,
  ChainConfig,
  ConsensusState,
  CoreEngineState,
  ExecutionLayer,
} from './types';

// ─────────────────────────────────────────────
// CHAIN CONFIG
// ─────────────────────────────────────────────

export const CHAIN_CONFIG: ChainConfig = {
  chainId: 'rtwo-mainnet-1',
  chainName: 'RTWO Chain',
  nativeCurrency: 'RTWO',
  symbol: 'RTWO',
  decimals: 18,
  blockTime: 3,
  maxBlockSize: 2097152,
  maxGasPerBlock: 30000000,
  consensusAlgorithm: 'BFT',
  epochLength: 100,
  slashingEnabled: true,
  governanceEnabled: true,
};

// ─────────────────────────────────────────────
// CONSENSUS STATE
// ─────────────────────────────────────────────

export const CONSENSUS_STATE: ConsensusState = {
  round: 1,
  step: 'precommit',
  height: 4827341,
  lockedBlock: '0x3f8a9c2d1e4b7f6a0c5d8e2f1a3b4c5d6e7f8a9b',
  validBlock: '0x3f8a9c2d1e4b7f6a0c5d8e2f1a3b4c5d6e7f8a9b',
  validators: ['val-001', 'val-002', 'val-003', 'val-004', 'val-005'],
  quorumReached: true,
  votingPower: 87.4,
};

// ─────────────────────────────────────────────
// BLOCKS
// ─────────────────────────────────────────────

export const MOCK_BLOCKS: Block[] = [
  {
    height: 4827341,
    hash: '0x3f8a9c2d1e4b7f6a0c5d8e2f1a3b4c5d6e7f8a9b',
    parentHash: '0x2e7b8c1d0f3a6e5b9c4d7f0a2b3c4d5e6f7a8b9c',
    stateRoot: '0xabc123def456789012345678901234567890abcd',
    txRoot: '0xdef456abc789012345678901234567890abcdef12',
    timestamp: 1751580000,
    proposer: 'val-001',
    transactions: ['0xtx001', '0xtx002', '0xtx003'],
    txCount: 247,
    gasUsed: 18432000,
    gasLimit: 30000000,
    size: 524288,
    reward: '2.000000000000000000',
    finalized: true,
  },
  {
    height: 4827340,
    hash: '0x2e7b8c1d0f3a6e5b9c4d7f0a2b3c4d5e6f7a8b9c',
    parentHash: '0x1d6a7b0c9e2f5d4a8b3c6e1f0a2b3c4d5e6f7a8b',
    stateRoot: '0xbcd234ef5678901234567890123456789012bcde',
    txRoot: '0xef5678bcd901234567890123456789012bcdef23',
    timestamp: 1751579997,
    proposer: 'val-003',
    transactions: ['0xtx004', '0xtx005'],
    txCount: 183,
    gasUsed: 14200000,
    gasLimit: 30000000,
    size: 401408,
    reward: '2.000000000000000000',
    finalized: true,
  },
  {
    height: 4827339,
    hash: '0x1d6a7b0c9e2f5d4a8b3c6e1f0a2b3c4d5e6f7a8b',
    parentHash: '0x0c5b6a9d8e1f4c3b7a2d5f0e1a2b3c4d5e6f7a8b',
    stateRoot: '0xcde345f6789012345678901234567890123cdef0',
    txRoot: '0xf6789cde012345678901234567890123cdef0134',
    timestamp: 1751579994,
    proposer: 'val-002',
    transactions: ['0xtx006', '0xtx007', '0xtx008', '0xtx009'],
    txCount: 312,
    gasUsed: 22100000,
    gasLimit: 30000000,
    size: 655360,
    reward: '2.000000000000000000',
    finalized: true,
  },
  {
    height: 4827338,
    hash: '0x0c5b6a9d8e1f4c3b7a2d5f0e1a2b3c4d5e6f7a8b',
    parentHash: '0xfb4a5c8d7e0f3b2a6c1e4d9f0a2b3c4d5e6f7a8b',
    stateRoot: '0xdef456789012345678901234567890123def0145',
    txRoot: '0x789012def345678901234567890123def0145678',
    timestamp: 1751579991,
    proposer: 'val-005',
    transactions: ['0xtx010'],
    txCount: 98,
    gasUsed: 7800000,
    gasLimit: 30000000,
    size: 196608,
    reward: '2.000000000000000000',
    finalized: true,
  },
  {
    height: 4827337,
    hash: '0xfb4a5c8d7e0f3b2a6c1e4d9f0a2b3c4d5e6f7a8b',
    parentHash: '0xea3b4c7d6f9e2a1b5d0c3e8f0a2b3c4d5e6f7a8b',
    stateRoot: '0xef5678901234567890123456789012345ef01256',
    txRoot: '0x901234ef5678901234567890123456789ef01267',
    timestamp: 1751579988,
    proposer: 'val-004',
    transactions: ['0xtx011', '0xtx012'],
    txCount: 156,
    gasUsed: 11900000,
    gasLimit: 30000000,
    size: 327680,
    reward: '2.000000000000000000',
    finalized: true,
  },
];

// ─────────────────────────────────────────────
// TRANSACTIONS
// ─────────────────────────────────────────────

export const MOCK_TRANSACTIONS: Transaction[] = [
  {
    hash: '0xabc123def456789012345678901234567890abcd',
    type: 'transfer',
    from: '0xRTWO1qpzry9x8gf2tvdw0s3jn54khce6mua7lmqqqxw',
    to: '0xRTWO1qqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqq',
    value: '1500.000000000000000000',
    fee: '0.002100000000000000',
    gasLimit: 21000,
    gasUsed: 21000,
    nonce: 847,
    status: 'confirmed',
    blockHeight: 4827341,
    blockHash: '0x3f8a9c2d1e4b7f6a0c5d8e2f1a3b4c5d6e7f8a9b',
    timestamp: 1751580000,
    confirmations: 12,
  },
  {
    hash: '0xbcd234ef5678901234567890123456789012bcde',
    type: 'stake',
    from: '0xRTWO1zg69v7ys4rnwrzquwqvt3x9renr5qn986ld6ql',
    to: '0xRTWO1valoper1qpzry9x8gf2tvdw0s3jn54khce6mua7l',
    value: '50000.000000000000000000',
    fee: '0.003500000000000000',
    gasLimit: 35000,
    gasUsed: 32100,
    nonce: 23,
    status: 'confirmed',
    blockHeight: 4827341,
    blockHash: '0x3f8a9c2d1e4b7f6a0c5d8e2f1a3b4c5d6e7f8a9b',
    timestamp: 1751580000,
    confirmations: 12,
  },
  {
    hash: '0xcde345f6789012345678901234567890123cdef0',
    type: 'governance_vote',
    from: '0xRTWO1x8gf2tvdw0s3jn54khce6mua7lmqqqxwzg69v7',
    to: '0xRTWO1governance_module_address_here_000000',
    value: '0.000000000000000000',
    fee: '0.001200000000000000',
    gasLimit: 12000,
    gasUsed: 11400,
    nonce: 156,
    status: 'confirmed',
    blockHeight: 4827340,
    blockHash: '0x2e7b8c1d0f3a6e5b9c4d7f0a2b3c4d5e6f7a8b9c',
    timestamp: 1751579997,
    confirmations: 13,
  },
  {
    hash: '0xdef456789012345678901234567890123def0145',
    type: 'transfer',
    from: '0xRTWO1tvdw0s3jn54khce6mua7lmqqqxwzg69v7ys4rn',
    to: '0xRTWO1s3jn54khce6mua7lmqqqxwzg69v7ys4rnwrzquw',
    value: '250.500000000000000000',
    fee: '0.002100000000000000',
    gasLimit: 21000,
    gasUsed: 21000,
    nonce: 1204,
    status: 'pending',
    timestamp: 1751580003,
    confirmations: 0,
  },
  {
    hash: '0xef5678901234567890123456789012345ef01256',
    type: 'contract_call',
    from: '0xRTWO1w0s3jn54khce6mua7lmqqqxwzg69v7ys4rnwrzq',
    to: '0xRTWO1contract_address_defi_protocol_000000',
    value: '0.000000000000000000',
    fee: '0.008400000000000000',
    gasLimit: 84000,
    gasUsed: 79200,
    nonce: 512,
    status: 'failed',
    blockHeight: 4827339,
    blockHash: '0x1d6a7b0c9e2f5d4a8b3c6e1f0a2b3c4d5e6f7a8b',
    timestamp: 1751579994,
    confirmations: 14,
    error: 'execution reverted: insufficient liquidity',
  },
];

// ─────────────────────────────────────────────
// VALIDATORS
// ─────────────────────────────────────────────

export const MOCK_VALIDATORS: Validator[] = [
  {
    id: 'val-001',
    address: '0xRTWO1valoper1qpzry9x8gf2tvdw0s3jn54khce6mua7l',
    moniker: 'RTWO Foundation Node',
    status: 'active',
    votingPower: 18420000,
    votingPowerPercent: 12.4,
    commission: 5.0,
    totalStake: '18420000.000000000000000000',
    selfStake: '2000000.000000000000000000',
    delegatorCount: 847,
    blocksProposed: 124832,
    blocksMissed: 12,
    uptime: 99.99,
    jailCount: 0,
    slashCount: 0,
    lastActive: 1751580000,
    rank: 1,
    website: 'https://rtwochain.io',
    description: 'Official RTWO Foundation validator node',
  },
  {
    id: 'val-002',
    address: '0xRTWO1valoper2zg69v7ys4rnwrzquwqvt3x9renr5qn986',
    moniker: 'Apex Staking',
    status: 'active',
    votingPower: 15200000,
    votingPowerPercent: 10.2,
    commission: 7.5,
    totalStake: '15200000.000000000000000000',
    selfStake: '1500000.000000000000000000',
    delegatorCount: 623,
    blocksProposed: 98441,
    blocksMissed: 28,
    uptime: 99.97,
    jailCount: 0,
    slashCount: 0,
    lastActive: 1751579997,
    rank: 2,
    website: 'https://apexstaking.io',
    description: 'Professional validator infrastructure',
  },
  {
    id: 'val-003',
    address: '0xRTWO1valoper3x8gf2tvdw0s3jn54khce6mua7lmqqqxw',
    moniker: 'NodeGuard Pro',
    status: 'active',
    votingPower: 12800000,
    votingPowerPercent: 8.6,
    commission: 6.0,
    totalStake: '12800000.000000000000000000',
    selfStake: '1200000.000000000000000000',
    delegatorCount: 512,
    blocksProposed: 87234,
    blocksMissed: 45,
    uptime: 99.95,
    jailCount: 1,
    slashCount: 0,
    lastActive: 1751579994,
    rank: 3,
    website: 'https://nodeguard.pro',
    description: 'Enterprise-grade validator operations',
  },
  {
    id: 'val-004',
    address: '0xRTWO1valoper4tvdw0s3jn54khce6mua7lmqqqxwzg69v7',
    moniker: 'Quantum Validators',
    status: 'active',
    votingPower: 9600000,
    votingPowerPercent: 6.5,
    commission: 8.0,
    totalStake: '9600000.000000000000000000',
    selfStake: '800000.000000000000000000',
    delegatorCount: 389,
    blocksProposed: 65123,
    blocksMissed: 89,
    uptime: 99.86,
    jailCount: 0,
    slashCount: 1,
    lastActive: 1751579991,
    rank: 4,
    website: 'https://quantumval.io',
    description: 'High-performance quantum-resistant infrastructure',
  },
  {
    id: 'val-005',
    address: '0xRTWO1valoper5w0s3jn54khce6mua7lmqqqxwzg69v7ys4r',
    moniker: 'StakeHouse',
    status: 'active',
    votingPower: 8100000,
    votingPowerPercent: 5.5,
    commission: 10.0,
    totalStake: '8100000.000000000000000000',
    selfStake: '600000.000000000000000000',
    delegatorCount: 298,
    blocksProposed: 54892,
    blocksMissed: 134,
    uptime: 99.75,
    jailCount: 0,
    slashCount: 0,
    lastActive: 1751579988,
    rank: 5,
    website: 'https://stakehouse.io',
    description: 'Community-focused staking service',
  },
  {
    id: 'val-006',
    address: '0xRTWO1valoper6s3jn54khce6mua7lmqqqxwzg69v7ys4rnw',
    moniker: 'Sigma Node',
    status: 'jailed',
    votingPower: 0,
    votingPowerPercent: 0,
    commission: 9.0,
    totalStake: '4200000.000000000000000000',
    selfStake: '400000.000000000000000000',
    delegatorCount: 156,
    blocksProposed: 32100,
    blocksMissed: 2847,
    uptime: 91.2,
    jailCount: 3,
    slashCount: 2,
    lastActive: 1751492400,
    rank: 0,
    description: 'Currently jailed due to excessive downtime',
  },
];

// ─────────────────────────────────────────────
// NETWORK NODES
// ─────────────────────────────────────────────

export const MOCK_NODES: NetworkNode[] = [
  {
    id: 'node-001',
    type: 'validator',
    status: 'online',
    host: 'val-01.rtwochain.io',
    port: 26656,
    region: 'US-East',
    version: 'v1.0.4',
    blockHeight: 4827341,
    syncProgress: 100,
    peers: 48,
    latencyMs: 12,
    cpuUsage: 34,
    memoryUsage: 58,
    diskUsage: 42,
    bandwidth: { in: 124, out: 98 },
    uptime: 8640000,
    lastSeen: 1751580000,
    isValidator: true,
    validatorId: 'val-001',
  },
  {
    id: 'node-002',
    type: 'full',
    status: 'online',
    host: 'full-01.rtwochain.io',
    port: 26656,
    region: 'EU-West',
    version: 'v1.0.4',
    blockHeight: 4827341,
    syncProgress: 100,
    peers: 62,
    latencyMs: 28,
    cpuUsage: 22,
    memoryUsage: 44,
    diskUsage: 67,
    bandwidth: { in: 89, out: 72 },
    uptime: 7776000,
    lastSeen: 1751580000,
    isValidator: false,
  },
  {
    id: 'node-003',
    type: 'rpc',
    status: 'online',
    host: 'rpc-01.rtwochain.io',
    port: 8545,
    region: 'AP-Southeast',
    version: 'v1.0.4',
    blockHeight: 4827341,
    syncProgress: 100,
    peers: 35,
    latencyMs: 45,
    cpuUsage: 67,
    memoryUsage: 72,
    diskUsage: 38,
    bandwidth: { in: 312, out: 287 },
    uptime: 6912000,
    lastSeen: 1751580000,
    isValidator: false,
  },
  {
    id: 'node-004',
    type: 'archive',
    status: 'syncing',
    host: 'archive-01.rtwochain.io',
    port: 26656,
    region: 'US-West',
    version: 'v1.0.3',
    blockHeight: 4821000,
    syncProgress: 99.86,
    peers: 28,
    latencyMs: 18,
    cpuUsage: 89,
    memoryUsage: 91,
    diskUsage: 78,
    bandwidth: { in: 456, out: 12 },
    uptime: 432000,
    lastSeen: 1751580000,
    isValidator: false,
  },
  {
    id: 'node-005',
    type: 'validator',
    status: 'warning',
    host: 'val-02.rtwochain.io',
    port: 26656,
    region: 'EU-Central',
    version: 'v1.0.4',
    blockHeight: 4827338,
    syncProgress: 99.99,
    peers: 19,
    latencyMs: 156,
    cpuUsage: 94,
    memoryUsage: 87,
    diskUsage: 55,
    bandwidth: { in: 67, out: 54 },
    uptime: 5184000,
    lastSeen: 1751579985,
    isValidator: true,
    validatorId: 'val-004',
  },
  {
    id: 'node-006',
    type: 'seed',
    status: 'offline',
    host: 'seed-01.rtwochain.io',
    port: 26656,
    region: 'AP-Northeast',
    version: 'v1.0.2',
    blockHeight: 4810000,
    syncProgress: 99.65,
    peers: 0,
    latencyMs: 0,
    cpuUsage: 0,
    memoryUsage: 0,
    diskUsage: 45,
    bandwidth: { in: 0, out: 0 },
    uptime: 0,
    lastSeen: 1751493600,
    isValidator: false,
  },
];

// ─────────────────────────────────────────────
// RPC ENDPOINTS
// ─────────────────────────────────────────────

export const MOCK_RPC_ENDPOINTS: RpcEndpoint[] = [
  {
    id: 'rpc-001',
    name: 'RTWO Public RPC',
    url: 'https://rpc.rtwochain.io',
    protocol: 'https',
    type: 'public',
    status: 'healthy',
    requestsPerSecond: 847,
    avgLatencyMs: 42,
    errorRate: 0.12,
    rateLimitPerMin: 300,
    authRequired: false,
    supportedMethods: [
      'rtwo_getBlock',
      'rtwo_getTransaction',
      'rtwo_getBalance',
      'eth_getBlockByNumber',
      'net_version',
    ],
  },
  {
    id: 'rpc-002',
    name: 'RTWO WebSocket',
    url: 'wss://ws.rtwochain.io',
    protocol: 'wss',
    type: 'public',
    status: 'healthy',
    requestsPerSecond: 234,
    avgLatencyMs: 18,
    errorRate: 0.05,
    rateLimitPerMin: 100,
    authRequired: false,
    supportedMethods: ['rtwo_subscribeBlocks', 'rtwo_subscribeTransactions'],
  },
  {
    id: 'rpc-003',
    name: 'Enterprise RPC',
    url: 'https://enterprise-rpc.rtwochain.io',
    protocol: 'https',
    type: 'enterprise',
    status: 'healthy',
    requestsPerSecond: 3420,
    avgLatencyMs: 8,
    errorRate: 0.01,
    rateLimitPerMin: 10000,
    authRequired: true,
    supportedMethods: [
      'rtwo_getBlock',
      'rtwo_getTransaction',
      'rtwo_getBalance',
      'rtwo_sendTransaction',
      'rtwo_getValidators',
      'rtwo_getNetworkInfo',
      'eth_getBlockByNumber',
      'eth_getTransactionByHash',
      'eth_sendRawTransaction',
      'eth_call',
      'eth_estimateGas',
    ],
  },
  {
    id: 'rpc-004',
    name: 'Internal RPC',
    url: 'http://internal-rpc.rtwochain.local',
    protocol: 'http',
    type: 'private',
    status: 'degraded',
    requestsPerSecond: 156,
    avgLatencyMs: 124,
    errorRate: 2.4,
    rateLimitPerMin: 50000,
    authRequired: true,
    supportedMethods: [
      'rtwo_getBlock',
      'rtwo_getTransaction',
      'rtwo_getBalance',
      'rtwo_sendTransaction',
    ],
  },
];

// ─────────────────────────────────────────────
// API KEYS
// ─────────────────────────────────────────────

export const MOCK_API_KEYS: ApiKey[] = [
  {
    id: 'key-001',
    name: 'DeFi Protocol Integration',
    key: 'rtwo_ent_••••••••••••••••••••••••••••••••',
    tier: 'enterprise',
    owner: 'DeFi Labs Inc.',
    permissions: ['read:blocks', 'read:transactions', 'write:transactions', 'read:validators'],
    requestsToday: 284721,
    requestsLimit: 1000000,
    createdAt: 1748880000,
    lastUsed: 1751580000,
    status: 'active',
  },
  {
    id: 'key-002',
    name: 'Explorer Backend',
    key: 'rtwo_int_••••••••••••••••••••••••••••••••',
    tier: 'internal',
    owner: 'RTWO Foundation',
    permissions: [
      'read:blocks',
      'read:transactions',
      'read:validators',
      'read:governance',
      'admin:read',
    ],
    requestsToday: 1847234,
    requestsLimit: 10000000,
    createdAt: 1746288000,
    lastUsed: 1751580000,
    status: 'active',
  },
  {
    id: 'key-003',
    name: 'Mobile Wallet SDK',
    key: 'rtwo_dev_••••••••••••••••••••••••••••••••',
    tier: 'developer',
    owner: 'WalletCo',
    permissions: ['read:blocks', 'read:transactions', 'write:transactions', 'read:balance'],
    requestsToday: 47823,
    requestsLimit: 100000,
    createdAt: 1749484800,
    lastUsed: 1751579800,
    status: 'active',
  },
  {
    id: 'key-004',
    name: 'Analytics Platform',
    key: 'rtwo_dev_••••••••••••••••••••••••••••••••',
    tier: 'developer',
    owner: 'ChainAnalytics',
    permissions: ['read:blocks', 'read:transactions', 'read:validators'],
    requestsToday: 98234,
    requestsLimit: 100000,
    createdAt: 1750089600,
    expiresAt: 1783238400,
    lastUsed: 1751579400,
    status: 'active',
  },
];

// ─────────────────────────────────────────────
// GOVERNANCE PROPOSALS
// ─────────────────────────────────────────────

export const MOCK_PROPOSALS: GovernanceProposal[] = [
  {
    id: 'prop-007',
    title: 'Increase Maximum Validator Set to 150',
    description:
      'This proposal seeks to expand the active validator set from 100 to 150 validators to improve decentralization and network resilience.',
    type: 'parameter_change',
    status: 'voting_period',
    proposer: '0xRTWO1qpzry9x8gf2tvdw0s3jn54khce6mua7lmqqqxw',
    depositAmount: '10000.000000000000000000',
    depositRequired: '10000.000000000000000000',
    votingStartTime: 1751493600,
    votingEndTime: 1751752800,
    votes: {
      yes: '89420000',
      no: '12300000',
      abstain: '4800000',
      noWithVeto: '1200000',
      totalVotingPower: '148500000',
      participation: 72.8,
    },
    createdAt: 1751407200,
    updatedAt: 1751580000,
  },
  {
    id: 'prop-008',
    title: 'RTWO Core Engine v1.1 Software Upgrade',
    description:
      'Scheduled upgrade to Core Engine v1.1 introducing improved BFT consensus, enhanced RPC performance, and new governance automation features.',
    type: 'software_upgrade',
    status: 'voting_period',
    proposer: '0xRTWO1zg69v7ys4rnwrzquwqvt3x9renr5qn986ld6ql',
    depositAmount: '10000.000000000000000000',
    depositRequired: '10000.000000000000000000',
    votingStartTime: 1751580000,
    votingEndTime: 1751839200,
    votes: {
      yes: '42100000',
      no: '3200000',
      abstain: '1800000',
      noWithVeto: '400000',
      totalVotingPower: '148500000',
      participation: 31.9,
    },
    createdAt: 1751493600,
    updatedAt: 1751580000,
  },
  {
    id: 'prop-006',
    title: 'Community Development Fund Allocation',
    description:
      'Allocate 500,000 RTWO from the community pool to fund ecosystem development grants for Q3 2026.',
    type: 'community_spend',
    status: 'passed',
    proposer: '0xRTWO1x8gf2tvdw0s3jn54khce6mua7lmqqqxwzg69v7',
    depositAmount: '10000.000000000000000000',
    depositRequired: '10000.000000000000000000',
    votingStartTime: 1750284000,
    votingEndTime: 1750543200,
    votes: {
      yes: '112400000',
      no: '8900000',
      abstain: '6200000',
      noWithVeto: '800000',
      totalVotingPower: '148500000',
      participation: 86.1,
    },
    enactmentHeight: 4800000,
    createdAt: 1750197600,
    updatedAt: 1750543200,
  },
  {
    id: 'prop-005',
    title: 'Reduce Minimum Commission Rate to 3%',
    description:
      'Lower the minimum validator commission from 5% to 3% to increase competition and benefit delegators.',
    type: 'parameter_change',
    status: 'rejected',
    proposer: '0xRTWO1tvdw0s3jn54khce6mua7lmqqqxwzg69v7ys4rn',
    depositAmount: '10000.000000000000000000',
    depositRequired: '10000.000000000000000000',
    votingStartTime: 1749679200,
    votingEndTime: 1749938400,
    votes: {
      yes: '34200000',
      no: '87600000',
      abstain: '12400000',
      noWithVeto: '4800000',
      totalVotingPower: '148500000',
      participation: 93.7,
    },
    createdAt: 1749592800,
    updatedAt: 1749938400,
  },
];

// ─────────────────────────────────────────────
// ALERTS
// ─────────────────────────────────────────────

export const MOCK_ALERTS: Alert[] = [
  {
    id: 'alert-001',
    title: 'Validator node-005 High CPU Usage',
    message:
      'Validator node val-02.rtwochain.io CPU usage at 94% for over 15 minutes. Performance degradation risk.',
    severity: 'warning',
    status: 'active',
    source: 'R4-NodeMonitor',
    layer: 'R4',
    timestamp: 1751579400,
  },
  {
    id: 'alert-002',
    title: 'Internal RPC Endpoint Degraded',
    message:
      'Internal RPC endpoint showing 2.4% error rate and 124ms average latency. Investigating root cause.',
    severity: 'warning',
    status: 'acknowledged',
    source: 'R5-RpcMonitor',
    layer: 'R5',
    timestamp: 1751578800,
    acknowledgedBy: 'ops-team',
  },
  {
    id: 'alert-003',
    title: 'Seed Node Offline',
    message:
      'Seed node seed-01.rtwochain.io has been offline for 24+ hours. Peer discovery may be impacted.',
    severity: 'critical',
    status: 'active',
    source: 'R4-NodeMonitor',
    layer: 'R4',
    timestamp: 1751493600,
  },
  {
    id: 'alert-004',
    title: 'Validator val-006 Jailed',
    message:
      'Validator Sigma Node (val-006) has been jailed due to excessive missed blocks (2847 missed).',
    severity: 'critical',
    status: 'acknowledged',
    source: 'R3-ValidatorManager',
    layer: 'R3',
    timestamp: 1751492400,
    acknowledgedBy: 'admin',
  },
  {
    id: 'alert-005',
    title: 'Archive Node Sync In Progress',
    message:
      'Archive node archive-01.rtwochain.io is syncing at 99.86%. Expected completion in ~2 hours.',
    severity: 'info',
    status: 'active',
    source: 'R4-SyncManager',
    layer: 'R4',
    timestamp: 1751580000,
  },
];

// ─────────────────────────────────────────────
// SECURITY EVENTS
// ─────────────────────────────────────────────

export const MOCK_SECURITY_EVENTS: SecurityEvent[] = [
  {
    id: 'sec-001',
    type: 'rate_limit_exceeded',
    severity: 'warning',
    source: '203.0.113.42',
    target: 'rpc-001',
    description:
      'IP 203.0.113.42 exceeded public RPC rate limit (300 req/min). Temporary block applied.',
    mitigated: true,
    timestamp: 1751579200,
  },
  {
    id: 'sec-002',
    type: 'suspicious_transaction',
    severity: 'warning',
    source: '0xRTWO1suspicious_address_flagged_by_monitor',
    description: 'Transaction pattern matches known wash-trading signature. Flagged for review.',
    mitigated: false,
    timestamp: 1751578400,
  },
  {
    id: 'sec-003',
    type: 'invalid_signature',
    severity: 'info',
    source: 'node-004',
    description:
      'Archive node submitted block with invalid signature during sync recovery. Auto-rejected.',
    mitigated: true,
    timestamp: 1751577600,
  },
];

// ─────────────────────────────────────────────
// AUDIT LOG
// ─────────────────────────────────────────────

export const MOCK_AUDIT_LOG: AuditEntry[] = [
  {
    id: 'audit-001',
    actor: 'admin@rtwochain.io',
    action: 'API_KEY_CREATED',
    resource: 'ApiKey',
    resourceId: 'key-003',
    result: 'success',
    ipAddress: '10.0.1.45',
    timestamp: 1751579800,
    details: { keyName: 'Mobile Wallet SDK', tier: 'developer' },
  },
  {
    id: 'audit-002',
    actor: 'ops@rtwochain.io',
    action: 'ALERT_ACKNOWLEDGED',
    resource: 'Alert',
    resourceId: 'alert-002',
    result: 'success',
    ipAddress: '10.0.1.23',
    timestamp: 1751579200,
    details: { alertTitle: 'Internal RPC Endpoint Degraded' },
  },
  {
    id: 'audit-003',
    actor: 'admin@rtwochain.io',
    action: 'VALIDATOR_JAILED',
    resource: 'Validator',
    resourceId: 'val-006',
    result: 'success',
    ipAddress: '10.0.1.45',
    timestamp: 1751492400,
    details: { reason: 'excessive_downtime', missedBlocks: 2847 },
  },
  {
    id: 'audit-004',
    actor: 'system',
    action: 'PROPOSAL_ENACTED',
    resource: 'GovernanceProposal',
    resourceId: 'prop-006',
    result: 'success',
    timestamp: 1750543200,
    details: { title: 'Community Development Fund Allocation', enactmentHeight: 4800000 },
  },
  {
    id: 'audit-005',
    actor: 'unknown@external.com',
    action: 'LOGIN_ATTEMPT',
    resource: 'AdminPanel',
    result: 'denied',
    ipAddress: '198.51.100.87',
    timestamp: 1751578000,
    details: { reason: 'invalid_credentials', attempts: 3 },
  },
];

// ─────────────────────────────────────────────
// AUTOMATION RULES
// ─────────────────────────────────────────────

export const MOCK_AUTOMATION_RULES: AutomationRule[] = [
  {
    id: 'auto-001',
    name: 'Auto-Jail Downtime Validators',
    description: 'Automatically jail validators that miss more than 500 consecutive blocks.',
    trigger: { type: 'metric_threshold', condition: 'consecutive_missed_blocks', value: 500 },
    actions: [
      { type: 'jail', target: 'validator' },
      { type: 'alert', params: { severity: 'critical' } },
    ],
    enabled: true,
    lastTriggered: 1751492400,
    triggerCount: 3,
    priority: 'critical',
    createdAt: 1746288000,
  },
  {
    id: 'auto-002',
    name: 'Epoch Reward Distribution',
    description: 'Automatically distribute staking rewards at the end of each epoch.',
    trigger: { type: 'event', condition: 'epoch_ended' },
    actions: [
      { type: 'governance_execute', params: { action: 'distribute_rewards' } },
      { type: 'log' },
    ],
    enabled: true,
    lastTriggered: 1751577600,
    triggerCount: 48241,
    priority: 'high',
    createdAt: 1746288000,
  },
  {
    id: 'auto-003',
    name: 'RPC Rate Limit Alert',
    description: 'Send alert when any RPC endpoint error rate exceeds 1%.',
    trigger: { type: 'metric_threshold', condition: 'rpc_error_rate', value: 1 },
    actions: [
      { type: 'alert', params: { severity: 'warning' } },
      { type: 'webhook', params: { url: 'https://hooks.rtwochain.io/alerts' } },
    ],
    enabled: true,
    lastTriggered: 1751578800,
    triggerCount: 12,
    priority: 'medium',
    createdAt: 1748880000,
  },
  {
    id: 'auto-004',
    name: 'Governance Proposal Enactment',
    description: 'Automatically enact passed governance proposals at the specified block height.',
    trigger: { type: 'block_height', condition: 'proposal_enactment_height' },
    actions: [
      { type: 'governance_execute', params: { action: 'enact_proposal' } },
      { type: 'log' },
      { type: 'alert', params: { severity: 'info' } },
    ],
    enabled: true,
    lastTriggered: 1750543200,
    triggerCount: 6,
    priority: 'critical',
    createdAt: 1746288000,
  },
];

// ─────────────────────────────────────────────
// SYSTEM METRICS (time series — stable values)
// ─────────────────────────────────────────────

export const MOCK_SYSTEM_METRICS: SystemMetrics[] = [
  {
    timestamp: 1751576400,
    cpu: 42,
    memory: 61,
    disk: 48,
    networkIn: 234,
    networkOut: 198,
    tps: 847,
    blockTime: 3.1,
    activeValidators: 100,
    pendingTx: 1247,
    mempoolSize: 2847,
  },
  {
    timestamp: 1751577600,
    cpu: 45,
    memory: 63,
    disk: 48,
    networkIn: 267,
    networkOut: 221,
    tps: 923,
    blockTime: 2.9,
    activeValidators: 100,
    pendingTx: 1389,
    mempoolSize: 3124,
  },
  {
    timestamp: 1751578800,
    cpu: 67,
    memory: 68,
    disk: 49,
    networkIn: 312,
    networkOut: 287,
    tps: 1247,
    blockTime: 3.2,
    activeValidators: 100,
    pendingTx: 2847,
    mempoolSize: 5234,
  },
  {
    timestamp: 1751580000,
    cpu: 51,
    memory: 65,
    disk: 49,
    networkIn: 289,
    networkOut: 243,
    tps: 1089,
    blockTime: 3.0,
    activeValidators: 100,
    pendingTx: 1923,
    mempoolSize: 4012,
  },
];

// ─────────────────────────────────────────────
// MEMPOOL STATE
// ─────────────────────────────────────────────

export const MOCK_MEMPOOL: MempoolState = {
  pendingCount: 1923,
  queuedCount: 847,
  totalSize: 4194304,
  avgGasPrice: '0.000000021',
  minGasPrice: '0.000000010',
  maxGasPrice: '0.000000250',
  oldestTxAge: 47,
  throughput: 1089,
};

// ─────────────────────────────────────────────
// NETWORK TOPOLOGY
// ─────────────────────────────────────────────

export const MOCK_NETWORK_TOPOLOGY: NetworkTopology = {
  totalNodes: 247,
  activeNodes: 241,
  validatorNodes: 100,
  fullNodes: 89,
  archiveNodes: 12,
  rpcNodes: 18,
  regions: [
    { name: 'US-East', nodeCount: 67, avgLatency: 14, status: 'healthy' },
    { name: 'EU-West', nodeCount: 58, avgLatency: 22, status: 'healthy' },
    { name: 'AP-Southeast', nodeCount: 43, avgLatency: 38, status: 'healthy' },
    { name: 'US-West', nodeCount: 34, avgLatency: 19, status: 'degraded' },
    { name: 'EU-Central', nodeCount: 29, avgLatency: 31, status: 'healthy' },
    { name: 'AP-Northeast', nodeCount: 16, avgLatency: 52, status: 'degraded' },
  ],
  avgLatency: 28,
};

// ─────────────────────────────────────────────
// SLASH EVENTS
// ─────────────────────────────────────────────

export const MOCK_SLASH_EVENTS: SlashEvent[] = [
  {
    id: 'slash-001',
    validator: 'val-006',
    reason: 'downtime',
    slashFactor: 0.0001,
    amountSlashed: '420.000000000000000000',
    blockHeight: 4820000,
    timestamp: 1751492400,
    jailed: true,
  },
  {
    id: 'slash-002',
    validator: 'val-004',
    reason: 'double_sign',
    slashFactor: 0.05,
    amountSlashed: '48000.000000000000000000',
    blockHeight: 4750000,
    timestamp: 1750284000,
    jailed: false,
  },
];

// ─────────────────────────────────────────────
// EPOCH INFO
// ─────────────────────────────────────────────

export const MOCK_CURRENT_EPOCH: EpochInfo = {
  epochNumber: 48241,
  startHeight: 4827300,
  endHeight: 4827400,
  totalRewards: '200.000000000000000000',
  distributedRewards: '0.000000000000000000',
  activeValidators: 100,
  totalStake: '148500000.000000000000000000',
  inflationRate: 7.5,
};

// ─────────────────────────────────────────────
// CORE ENGINE STATE
// ─────────────────────────────────────────────

export const MOCK_EXECUTION_LAYERS: Record<string, ExecutionLayer> = {
  R1: {
    id: 'R1',
    name: 'Foundation & Initialization',
    description:
      'Core engine bootstrap, initialization lifecycle, configuration loading, and system readiness',
    status: 'initializing',
    health: 'offline',
    version: 'v1.0.4',
    lastHeartbeat: 0,
    services: [
      {
        id: 'r1-consensus',
        name: 'BFT Consensus Engine',
        layer: 'R1',
        status: 'offline',
        version: 'v1.0.4',
        dependencies: [],
      },
      {
        id: 'r1-block-producer',
        name: 'Block Producer',
        layer: 'R1',
        status: 'offline',
        version: 'v1.0.4',
        dependencies: ['r1-consensus'],
      },
      {
        id: 'r1-state-machine',
        name: 'State Machine',
        layer: 'R1',
        status: 'offline',
        version: 'v1.0.4',
        dependencies: ['r1-block-producer'],
      },
      {
        id: 'r1-finality',
        name: 'Finality Tracker',
        layer: 'R1',
        status: 'offline',
        version: 'v1.0.4',
        dependencies: ['r1-consensus'],
      },
    ],
    metrics: { requestsPerSecond: 0, errorRate: 0, latencyMs: 0, uptime: 0, activeConnections: 0 },
  },
  R2: {
    id: 'R2',
    name: 'Synchronization',
    description:
      'Chain synchronization, block propagation, peer state alignment, and catch-up protocols',
    status: 'initializing',
    health: 'offline',
    version: 'v1.0.4',
    lastHeartbeat: 0,
    services: [
      {
        id: 'r2-mempool',
        name: 'Mempool Manager',
        layer: 'R2',
        status: 'offline',
        version: 'v1.0.4',
        dependencies: [],
      },
      {
        id: 'r2-validator',
        name: 'Transaction Validator',
        layer: 'R2',
        status: 'offline',
        version: 'v1.0.4',
        dependencies: ['r2-mempool'],
      },
      {
        id: 'r2-executor',
        name: 'Transaction Executor',
        layer: 'R2',
        status: 'offline',
        version: 'v1.0.4',
        dependencies: ['r2-validator', 'r1-state-machine'],
      },
      {
        id: 'r2-broadcaster',
        name: 'Transaction Broadcaster',
        layer: 'R2',
        status: 'offline',
        version: 'v1.0.4',
        dependencies: ['r2-mempool'],
      },
    ],
    metrics: { requestsPerSecond: 0, errorRate: 0, latencyMs: 0, uptime: 0, activeConnections: 0 },
  },
  R3: {
    id: 'R3',
    name: 'Core Orchestrator',
    description:
      'Unified execution orchestration, service coordination, pipeline management, and lifecycle control',
    status: 'initializing',
    health: 'offline',
    version: 'v1.0.4',
    lastHeartbeat: 0,
    services: [
      {
        id: 'r3-validator-mgr',
        name: 'Validator Manager',
        layer: 'R3',
        status: 'offline',
        version: 'v1.0.4',
        dependencies: ['r1-consensus'],
      },
      {
        id: 'r3-staking',
        name: 'Staking Engine',
        layer: 'R3',
        status: 'offline',
        version: 'v1.0.4',
        dependencies: ['r3-validator-mgr'],
      },
      {
        id: 'r3-slashing',
        name: 'Slashing Module',
        layer: 'R3',
        status: 'offline',
        version: 'v1.0.4',
        dependencies: ['r3-validator-mgr'],
      },
      {
        id: 'r3-rewards',
        name: 'Reward Distributor',
        layer: 'R3',
        status: 'offline',
        version: 'v1.0.4',
        dependencies: ['r3-staking'],
      },
    ],
    metrics: { requestsPerSecond: 0, errorRate: 0, latencyMs: 0, uptime: 0, activeConnections: 0 },
  },
  R4: {
    id: 'R4',
    name: 'Blockchain Protocol Engine',
    description:
      'Block creation, transaction execution, fee calculation, state transitions, and protocol lifecycle',
    status: 'initializing',
    health: 'offline',
    version: 'v1.0.4',
    lastHeartbeat: 0,
    services: [
      {
        id: 'r4-p2p',
        name: 'P2P Network Manager',
        layer: 'R4',
        status: 'offline',
        version: 'v1.0.4',
        dependencies: [],
      },
      {
        id: 'r4-sync',
        name: 'Sync Manager',
        layer: 'R4',
        status: 'offline',
        version: 'v1.0.4',
        dependencies: ['r4-p2p'],
      },
      {
        id: 'r4-peer-discovery',
        name: 'Peer Discovery',
        layer: 'R4',
        status: 'offline',
        version: 'v1.0.4',
        dependencies: ['r4-p2p'],
      },
      {
        id: 'r4-topology',
        name: 'Topology Manager',
        layer: 'R4',
        status: 'offline',
        version: 'v1.0.4',
        dependencies: ['r4-p2p'],
      },
    ],
    metrics: { requestsPerSecond: 0, errorRate: 0, latencyMs: 0, uptime: 0, activeConnections: 0 },
  },
  R5: {
    id: 'R5',
    name: 'Infrastructure & Networking',
    description:
      'P2P networking, peer discovery, topology management, RPC services, and developer APIs',
    status: 'initializing',
    health: 'offline',
    version: 'v1.0.4',
    lastHeartbeat: 0,
    services: [
      {
        id: 'r5-rpc-http',
        name: 'HTTP RPC Server',
        layer: 'R5',
        status: 'offline',
        version: 'v1.0.4',
        dependencies: ['r2-executor'],
      },
      {
        id: 'r5-rpc-ws',
        name: 'WebSocket Server',
        layer: 'R5',
        status: 'offline',
        version: 'v1.0.4',
        dependencies: ['r2-executor'],
      },
      {
        id: 'r5-api-gateway',
        name: 'API Gateway',
        layer: 'R5',
        status: 'offline',
        version: 'v1.0.4',
        dependencies: ['r5-rpc-http'],
      },
      {
        id: 'r5-rate-limiter',
        name: 'Rate Limiter',
        layer: 'R5',
        status: 'offline',
        version: 'v1.0.4',
        dependencies: [],
      },
    ],
    metrics: { requestsPerSecond: 0, errorRate: 0, latencyMs: 0, uptime: 0, activeConnections: 0 },
  },
  R6: {
    id: 'R6',
    name: 'Security & Automation',
    description:
      'Security monitoring, threat detection, governance, automation rules, and operational safeguards',
    status: 'initializing',
    health: 'offline',
    version: 'v1.0.4',
    lastHeartbeat: 0,
    services: [
      {
        id: 'r6-governance',
        name: 'Governance Engine',
        layer: 'R6',
        status: 'offline',
        version: 'v1.0.4',
        dependencies: ['r3-validator-mgr'],
      },
      {
        id: 'r6-voting',
        name: 'Voting Module',
        layer: 'R6',
        status: 'offline',
        version: 'v1.0.4',
        dependencies: ['r6-governance'],
      },
      {
        id: 'r6-automation',
        name: 'Automation Engine',
        layer: 'R6',
        status: 'offline',
        version: 'v1.0.4',
        dependencies: [],
      },
      {
        id: 'r6-parameter',
        name: 'Parameter Controller',
        layer: 'R6',
        status: 'offline',
        version: 'v1.0.4',
        dependencies: ['r6-governance'],
      },
    ],
    metrics: { requestsPerSecond: 0, errorRate: 0, latencyMs: 0, uptime: 0, activeConnections: 0 },
  },
  R7: {
    id: 'R7',
    name: 'Enterprise Services',
    description:
      'Enterprise administration, audit logging, metrics collection, alerting, and operational tooling',
    status: 'initializing',
    health: 'offline',
    version: 'v1.0.4',
    lastHeartbeat: 0,
    services: [
      {
        id: 'r7-security',
        name: 'Security Monitor',
        layer: 'R7',
        status: 'offline',
        version: 'v1.0.4',
        dependencies: [],
      },
      {
        id: 'r7-alerting',
        name: 'Alert Manager',
        layer: 'R7',
        status: 'offline',
        version: 'v1.0.4',
        dependencies: ['r7-security'],
      },
      {
        id: 'r7-audit',
        name: 'Audit Logger',
        layer: 'R7',
        status: 'offline',
        version: 'v1.0.4',
        dependencies: [],
      },
      {
        id: 'r7-metrics',
        name: 'Metrics Collector',
        layer: 'R7',
        status: 'offline',
        version: 'v1.0.4',
        dependencies: [],
      },
      {
        id: 'r7-admin',
        name: 'Enterprise Admin',
        layer: 'R7',
        status: 'offline',
        version: 'v1.0.4',
        dependencies: ['r7-security', 'r7-audit'],
      },
    ],
    metrics: { requestsPerSecond: 0, errorRate: 0, latencyMs: 0, uptime: 0, activeConnections: 0 },
  },
};

export const MOCK_CORE_ENGINE_STATE: CoreEngineState = {
  status: 'running',
  version: 'v1.0.4',
  chainId: 'rtwo-mainnet-1',
  startedAt: 1746288000,
  uptime: 5292000,
  layers: MOCK_EXECUTION_LAYERS as CoreEngineState['layers'],
  activeAlerts: 3,
  criticalAlerts: 2,
  totalServices: 26,
  healthyServices: 23,
  currentBlock: 4827341,
  networkTps: 1089,
  totalValidators: 106,
  activeValidators: 100,
};
